/**
 * HOW THIS COMPANY WRITES MONEY.
 *
 * The twin of `companyClock`, and built the same way for the same reason. That
 * one exists because "today" was read from UTC in a hundred places; this one
 * exists because every money figure on the platform was written like this:
 *
 *     new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })
 *
 * — 67 times in Motor Trade and ERP alone, with the locale and the currency as
 * literals beside each other. The tenant has carried `locale` and
 * `currencyCode` since the beginning and nothing read them.
 *
 * Nothing is broken today: every tenant is British, so every one of those 67
 * is correct. What it costs is that the decision is written down 67 times, so
 * it is 67 places to drift and 67 edits the day a tenant trades in euros —
 * which is the kind of change that gets quoted as a week and is really one
 * line, if it is one line.
 *
 * ── IT READS THE CACHE, NOT A HOOK ────────────────────────────────────────
 *
 * Exactly as `companyTimeZone()` does, and for the same reason: money is
 * formatted in module-level helpers, event handlers, CSV builders and document
 * generators, none of which can call a hook. A component that must RE-RENDER
 * when the answer arrives uses `useCompanyMoney()` below; everything else
 * reads the cache and falls back.
 *
 * ── THE FALLBACK IS GB, AND THAT IS WHY THIS IS SAFE TO LAND ──────────────
 *
 * Until the query answers — and in every test that never seeds it — this is
 * `en-GB` and `GBP`, which is byte-for-byte what the 67 literals produced. So
 * the 91 test files asserting a pound sign keep passing, and nothing a user
 * sees changes on the day this lands. The behaviour only differs for a tenant
 * who is actually configured otherwise, which is the point.
 */
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from './api';
import { queryClient } from './queryClient';

/** What the 67 literals said, and what every GB tenant still gets. */
export const DEFAULT_COMPANY_LOCALE = 'en-GB';
export const DEFAULT_COMPANY_CURRENCY = 'GBP';

/**
 * Its own key, not the time zone's.
 *
 * They are cousins, not the same fact: one says WHEN, the other says HOW IT IS
 * WRITTEN. Sharing a key would mean a screen that needs the currency refetching
 * the zone, and a test seeding one silently deciding the other.
 */
export const COMPANY_FORMATTING_KEY = ['tenant', 'formatting'] as const;

export interface CompanyFormatting {
  locale: string;
  currencyCode: string;
  countryCode: string;
  /** The company's own region, for a screen with no branch picked. */
  region: string;
}

/** One cached read of the company's formatting and region, shared by every hook that needs it. */
const companyFormattingQuery = {
  queryKey: COMPANY_FORMATTING_KEY,
  queryFn: () => apiFetch<CompanyFormatting>('/api/tenant/formatting'),
  staleTime: 60 * 60 * 1000,
  refetchOnWindowFocus: false,
};

/** The company's own region, null until known: with no branch picked a screen shows the company's, not England's. */
export function useCompanyRegion(): string | null {
  return useQuery(companyFormattingQuery).data?.region ?? null;
}

function cached(): CompanyFormatting | undefined {
  return queryClient.getQueryData<CompanyFormatting>(COMPANY_FORMATTING_KEY);
}

/** The company's locale, for code outside a component. */
export function companyLocale(): string {
  return cached()?.locale ?? DEFAULT_COMPANY_LOCALE;
}

/** The company's country, or null until it is known: never assumed to be the UK. */
export function companyCountry(): string | null {
  return cached()?.countryCode ?? null;
}

/** The company's currency, for code outside a component. */
export function companyCurrency(): string {
  return cached()?.currencyCode ?? DEFAULT_COMPANY_CURRENCY;
}

/** A currency picker's choices: the company's own first, then the others the server's rate table converts. */
export function currencyChoices(): string[] {
  const base = companyCurrency();
  return [base, ...['GBP', 'EUR', 'USD'].filter((c) => c !== base)];
}

/**
 * Money, the way this company writes it.
 *
 * The replacement for `new Intl.NumberFormat('en-GB', { style: 'currency',
 * currency: 'GBP' }).format(n)`.
 *
 * `maximumFractionDigits` is NOT set here. Several of the call sites this
 * replaces passed it and several did not, which is a real difference — a
 * stock list rounding to whole pounds is a choice — so it stays a parameter
 * rather than being flattened to whichever the first converted file happened
 * to use.
 */
export function formatMoney(
  value: number,
  options: Intl.NumberFormatOptions = {},
): string {
  return new Intl.NumberFormat(companyLocale(), {
    style: 'currency',
    currency: companyCurrency(),
    ...options,
  }).format(value);
}

/** A plain number, grouped the way this company groups them. */
export function formatNumber(
  value: number,
  options: Intl.NumberFormatOptions = {},
): string {
  return new Intl.NumberFormat(companyLocale(), options).format(value);
}

/**
 * For a component that must re-render when the answer arrives.
 *
 * `ready` is false until it does, so a screen can hold a figure back rather
 * than print it in pounds and change it to euros a moment later — which is
 * worse than a brief dash, because the first one looks like an answer.
 */
export function useCompanyMoney(): {
  locale: string;
  currencyCode: string;
  /** Null until it is known. */
  countryCode: string | null;
  ready: boolean;
  format: (value: number, options?: Intl.NumberFormatOptions) => string;
} {
  const q = useQuery(companyFormattingQuery);
  const locale = q.data?.locale ?? DEFAULT_COMPANY_LOCALE;
  const currencyCode = q.data?.currencyCode ?? DEFAULT_COMPANY_CURRENCY;
  return {
    locale,
    currencyCode,
    countryCode: q.data?.countryCode ?? null,
    ready: q.data !== undefined,
    format: (value, options = {}) =>
      new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode, ...options })
        .format(value),
  };
}

/**
 * JUST THE SYMBOL — for the accountancy forms Intl will not write.
 *
 * `style: 'currency'` cannot produce `(£1,234)` for a negative, or `+£1,234`
 * against a budget. Accounts wants both, so about thirty helpers across ERP
 * build the string themselves:
 *
 *     '£' + n.toLocaleString('en-GB', { maximumFractionDigits: 0 })
 *
 * That is a THIRD shape of the same hardcoding, and the first guard I wrote
 * did not see it: it looked for `Intl.NumberFormat('xx-XX'` and for
 * `currency: 'GBP'`, and this is neither. It reported both sections clean
 * while thirty call sites still had the pound sign typed into them — the
 * same failure as the "0 unresolvable" i18n metric, a number that counted
 * what it could see and called the rest absent.
 *
 * So the bracket and the sign stay with the caller, who has a real reason for
 * them, and only the two literals move here.
 *
 * ── narrowSymbol, AND IT IS NOT A DETAIL ──────────────────────────────────
 *
 * Every one of those thirty helpers picked its symbol from the same hand-
 * written table: `currency === 'EUR' ? '€' : currency === 'USD' ? '$' : '£'`.
 *
 * Without `currencyDisplay: 'narrowSymbol'` this function returns what en-GB
 * calls a dollar, which is `US$` — so swapping the table for this would have
 * quietly turned every `$1,200` row in the ledger into `US$1,200`. Nobody
 * asked for that, and a sweep whose job is to change nothing visible has no
 * business changing it.
 *
 * narrowSymbol gives £, € and $ — the table, exactly, for the three it knew,
 * and a sensible answer for the hundred-odd it did not.
 *
 * Falls back to the code itself (`SEK` rather than nothing) if a locale has no
 * symbol for a currency — a figure reading `SEK1,234` is odd but still an
 * amount, where a bare `1,234` on a page of pounds is a wrong one.
 */
export function companyCurrencySymbol(currency?: string): string {
  const code = currency ?? companyCurrency();
  const part = new Intl.NumberFormat(companyLocale(), {
    style: 'currency',
    currency: code,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  })
    .formatToParts(0)
    .find((p) => p.type === 'currency');
  return part?.value ?? code;
}
